import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography
} from "@mui/material";

import type { LensProtocol } from "../../../../data/hooks/useLensProtocols";
import { CONVERSATION_LENS } from "../../../../utils/terminology";

interface LensAttributeSelectorProps {
  lens: string | null;
  attribute: string | null;
  lensProtocols: LensProtocol[];
  attributes: string[];
  isLoadingLenses: boolean;
  isLoadingAttributes: boolean;
  onLensChange: (lens: string | null) => void;
  onAttributeChange: (attribute: string | null) => void;
}

interface LensFieldProps {
  lens: string | null;
  lensProtocols: LensProtocol[];
  isLoadingLenses: boolean;
  onChange: (e: SelectChangeEvent<string>) => void;
}

const LensField = ({ lens, lensProtocols, isLoadingLenses, onChange }: LensFieldProps): React.ReactElement => (
  <Box>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      {CONVERSATION_LENS} <Typography component="span" color="error.main">*</Typography>
    </Typography>
    <Select
      fullWidth
      size="small"
      value={lens ?? ""}
      onChange={onChange}
      displayEmpty
      disabled={isLoadingLenses}
      startAdornment={isLoadingLenses ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
    >
      <MenuItem value="" disabled>
        Select a {CONVERSATION_LENS.toLowerCase()}
      </MenuItem>
      {lensProtocols.map((p) => (
        <MenuItem key={p.fullPath} value={p.name}>
          {p.name}
        </MenuItem>
      ))}
    </Select>
  </Box>
);

interface AttributeFieldProps {
  lens: string | null;
  attribute: string | null;
  attributes: string[];
  isLoadingAttributes: boolean;
  onChange: (e: SelectChangeEvent<string>) => void;
}

const AttributeField = ({ lens, attribute, attributes, isLoadingAttributes, onChange }: AttributeFieldProps): React.ReactElement => (
  <Box>
    <Typography variant="subtitle2" sx={{ mb: 1 }} color={!lens ? "text.disabled" : "text.primary"}>
      Attribute
    </Typography>
    <Select
      fullWidth
      size="small"
      value={attribute ?? ""}
      onChange={onChange}
      displayEmpty
      disabled={lens === null || isLoadingAttributes}
      startAdornment={isLoadingAttributes ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
      renderValue={(selected) => selected || ""}
    >
      <MenuItem value="">
        <Typography color="text.secondary">None</Typography>
      </MenuItem>
      {attributes.map((attr) => (
        <MenuItem key={attr} value={attr}>
          {attr}
        </MenuItem>
      ))}
    </Select>
    {attribute === null && lens !== null && (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
        The overall conversation score will be used for ranking
      </Typography>
    )}
  </Box>
);

const LensAttributeSelector = ({
  lens,
  attribute,
  lensProtocols,
  attributes,
  isLoadingLenses,
  isLoadingAttributes,
  onLensChange,
  onAttributeChange
}: LensAttributeSelectorProps): React.ReactElement => {
  const handleLensChange = (e: SelectChangeEvent<string>): void => {
    onLensChange(e.target.value);
  };

  const handleAttributeChange = (e: SelectChangeEvent<string>): void => {
    onAttributeChange(e.target.value || null);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <LensField
        lens={lens}
        lensProtocols={lensProtocols}
        isLoadingLenses={isLoadingLenses}
        onChange={handleLensChange}
      />
      <AttributeField
        lens={lens}
        attribute={attribute}
        attributes={attributes}
        isLoadingAttributes={isLoadingAttributes}
        onChange={handleAttributeChange}
      />
    </Box>
  );
};

export default LensAttributeSelector;
